import { Typography } from '@mui/material';

import { useEffect, useMemo, useState } from 'react';
import { formatTimestampInTimezone } from 'utils/time';
import styles from './SwellSignaturePoc.module.scss';

interface Props {
  imageUrls?: string[] | null;
  timestamp?: Date;
  similarityPercentage?: number;
  utcOffset?: number;
  surfHeight?: string;
  rating?: string;
  debugMode?: boolean;
}

const getDateFormat = (debugMode?: boolean) => {
  if (debugMode) {
    return 'HH:mm, MMM do yyyy';
  }
  return 'MMM do yyyy';
};

export const CamThumbImage = ({
  imageUrls,
  timestamp,
  similarityPercentage,
  utcOffset,
  surfHeight,
  rating,
  debugMode,
}: Props) => {
  const [hovered, setHovered] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [intervalId, setIntervalId] = useState<any>(null);

  useEffect(() => {
    if (hovered) {
      if (!intervalId) {
        const id = setInterval(() => {
          setCurrentImageIndex((prevIndex) => {
            if (imageUrls && prevIndex === imageUrls.length - 1) {
              return 0;
            }
            return prevIndex + 1;
          });
        }, 500);

        setIntervalId(id);
      }
    } else {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  }, [hovered, imageUrls, intervalId]);

  const images = useMemo(
    () =>
      imageUrls?.map((url, index) => (
        <img
          style={currentImageIndex === index ? { display: 'block' } : { display: 'none' }}
          key={`${timestamp}-${url}`}
          className={styles.predictedImage}
          src={url}
        />
      )),
    [currentImageIndex, imageUrls, timestamp],
  );

  return (
    <div
      className={styles.thumbImageWrapper}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {images}
      {!imageUrls?.length && (
        <div className={styles.noImageAvailable}>
          <Typography variant="body1">No Signature Match Available :(</Typography>
        </div>
      )}
      <div className={styles.hoveredOverlay}>
        <Typography color="common.white" variant="body1">
          {similarityPercentage}&#37; match
        </Typography>
        {hovered && debugMode && (
          <Typography color="common.white" variant="body1">
            {surfHeight} {rating}
          </Typography>
        )}
        {hovered && (
          <Typography color="common.white" variant="body1">
            {timestamp &&
              formatTimestampInTimezone(
                timestamp.getTime() / 1000,
                utcOffset || 0,
                getDateFormat(debugMode),
              )}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default CamThumbImage;
